import React, { FunctionComponent } from 'react';
import { SbBlokData, storyblokEditable } from '@storyblok/react';

import { Carousel, PressCard } from '@savebetter/components';

import { LinkProps } from './Link';
import { MediaProps } from './Media';
import { PageSectionBlok, PageSectionProps } from './PageSection';
import { PageSectionDescription } from './PageSectionDescription';
import { PageSectionHeader } from './PageSectionHeader';
import { Section } from './Section';

interface PressRelease extends SbBlokData {
  media: MediaProps[];
  text: string;
  link: LinkProps[];
  heading?: string;
}

interface Blok extends PageSectionBlok {
  reference: {
    content: {
      global: {
        title: string;
        description: string;
        pressReleases: PressRelease[];
        hasPaddingBottom: boolean;
        hasPaddingTop: boolean;
        hasBackground: boolean;
        hasBorder: boolean;
      }[];
    };
  };
  title?: string;
  description?: string;
  pressReleases?: PressRelease[];
}

interface PressRecognitionProps extends PageSectionProps {
  blok: Blok;
}

interface PressCardContainerProps {
  release: PressRelease;
  index: number;
}

// Note:- Container needs to get the props from Carousel that Blok doesn't pass.
const PressCardContainer: FunctionComponent<React.PropsWithChildren<PressCardContainerProps>> = ({
  release,
  index,
  ...restProps
}) => {
  const { media = [], text, link = [], heading } = release;
  return (
    <PressCard
      {...storyblokEditable(release)}
      logoSrc={media.length > 0 ? media[0].img : ''}
      logoAlt={media.length > 0 ? media[0].alt : ''}
      text={text}
      linkHref={link.length > 0 ? link[0].url : ''}
      linkText={link.length > 0 ? link[0].text : ''}
      linkTarget={link[0] && link[0].target}
      tabIndex={index}
      heading={heading}
      {...restProps}
    />
  );
};

export const PressRecognition: FunctionComponent<React.PropsWithChildren<PressRecognitionProps>> = ({
  blok,
  sectionClassName
}) => {
  const isRefBlok = blok.reference;
  const { pressReleases = [] } = isRefBlok ? blok.reference.content.global[0] : blok;
  const slides = pressReleases.map((release, index) => (
    <PressCardContainer key={release._uid} release={release} index={index} />
  ));

  const { hasBackground, hasBorder, title, description, hasPaddingTop, hasPaddingBottom } = isRefBlok
    ? blok.reference.content.global[0]
    : blok;

  return (
    <Section
      width="md"
      hasBackground={hasBackground}
      hasBorder={hasBorder}
      className={sectionClassName}
      blok={blok}
      hasPaddingBottom={hasPaddingBottom}
      hasPaddingTop={hasPaddingTop}
    >
      <PageSectionHeader>{title}</PageSectionHeader>
      <PageSectionDescription>{description}</PageSectionDescription>
      <Carousel contentLabel="Press releases" slides={slides} className="mb-16" useDefaultSlideWrapper={false} />
    </Section>
  );
};
